import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const BulkEditModuleModal = ({ onClose, onSubmit, selectedModules = [] }) => {
  const [formData, setFormData] = useState({
    category: '',
    areaUse: '',
    status: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const categoryOptions = [
    { value: '', label: 'Keep Existing' },
    { value: 'toilet-pod', label: 'Toilet Pod' },
    { value: 'kitchen-pod', label: 'Kitchen Pod' },
    { value: 'bathroom-pod', label: 'Bathroom Pod' },
    { value: 'utility-pod', label: 'Utility Pod' },
    { value: 'custom', label: 'Custom Module' }
  ];

  const areaUseOptions = [
    { value: '', label: 'Keep Existing' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'hospitality', label: 'Hospitality' }
  ];

  const statusOptions = [
    { value: '', label: 'Keep Existing' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'delayed', label: 'Delayed' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Filter out empty values (keep existing)
    const updates = {};
    if (formData?.category) updates.category = formData?.category;
    if (formData?.areaUse) updates.areaUse = formData?.areaUse;
    if (formData?.status) updates.status = formData?.status;

    // Check if any field is selected for update
    if (Object.keys(updates)?.length === 0) {
      return;
    }

    setSubmitting(true);
    await onSubmit(updates);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Edit" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Bulk Edit Modules</h2>
              <p className="text-sm text-muted-foreground">
                Editing {selectedModules?.length} {selectedModules?.length === 1 ? 'module' : 'modules'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {/* Info Banner */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-medium mb-1">Bulk Edit Instructions</p>
                  <p className="text-muted-foreground">
                    Select the fields you want to update. Fields set to "Keep Existing" will not be changed.
                    All selected modules will be updated with the same values.
                  </p>
                </div>
              </div>
            </div>

            {/* Selected Modules List */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Selected Modules</label>
              <div className="bg-muted/30 rounded-lg p-3 max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {selectedModules?.map((module) => (
                    <div key={module?.id} className="text-sm text-foreground flex items-center gap-2">
                      <Icon name="Package" size={14} color="var(--color-primary)" />
                      <span>{module?.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Category */}
            <Select
              label="Category"
              placeholder="Select category"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleInputChange('category', value)}
            />

            {/* Area Use */}
            <Select
              label="Area Use"
              placeholder="Select area use"
              options={areaUseOptions}
              value={formData?.areaUse}
              onChange={(value) => handleInputChange('areaUse', value)}
            />

            {/* Status */}
            <Select
              label="Status"
              placeholder="Select status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleInputChange('status', value)}
            />
          </div>
        </form>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            iconName="Save"
          >
            Update {selectedModules?.length} {selectedModules?.length === 1 ? 'Module' : 'Modules'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModuleModal;